import React from 'react';
import { format } from 'date-fns';
import { el } from 'date-fns/locale';

const STEPS = [
  { key: 'NEW', icon: '🆕', label: 'Νέα' },
  { key: 'IN_PROGRESS', icon: '⚙️', label: 'Σε εξέλιξη' },
  { key: 'READY', icon: '✅', label: 'Έτοιμη' },
  { key: 'DELIVERED', icon: '📦', label: 'Παραδόθηκε' },
];

const STEP_INDEX = { NEW: 0, IN_PROGRESS: 1, READY: 2, DELIVERED: 3 };

// Edits are logged in the same history table with to_status = 'EDITED'.
// They are NOT a status of the order — the order stays NEW — so they're
// rendered as a separate list under the stepper rather than as a step.
function EditLog({ history }) {
  const edits = history.filter((h) => h.to_status === 'EDITED');
  if (edits.length === 0) return null;
  return (
    <div style={{
      marginTop: 10,
      background: '#fffbeb',
      border: '1px solid #fde68a',
      borderRadius: 10,
      padding: '10px 14px',
    }}>
      <div style={{
        fontSize: 11,
        fontWeight: 700,
        color: '#92400e',
        textTransform: 'uppercase',
        letterSpacing: 0.4,
        marginBottom: 6,
      }}>
        ✏️ Τροποποιήσεις ({edits.length})
      </div>
      {edits.map((h) => (
        <div key={h.id} style={{ fontSize: 12, color: '#78350f', padding: '2px 0' }}>
          {format(new Date(h.changed_at), 'd MMM yyyy, HH:mm', { locale: el })}
          {h.changed_by_name && ` · ${h.changed_by_name}`}
          {h.note && ` · ${h.note}`}
        </div>
      ))}
    </div>
  );
}

/**
 * Visual horizontal timeline for an order's status.
 *
 * Props:
 *   currentStatus: 'NEW' | 'IN_PROGRESS' | 'READY' | 'DELIVERED' | 'CANCELLED'
 *   history:       [{ to_status, changed_at, changed_by_name, note }]
 */
export default function StatusTimeline({ currentStatus, history = [] }) {
  // CANCELLED gets a special compact treatment
  if (currentStatus === 'CANCELLED') {
    const cancelEntry = history.find((h) => h.to_status === 'CANCELLED');
    return (
      <>
        <div style={{
          background: '#fee2e2',
          border: '1px solid #fca5a5',
          borderRadius: 10,
          padding: '14px 18px',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}>
          <div style={{ fontSize: 28 }}>❌</div>
          <div>
            <div style={{ fontWeight: 600, color: '#991b1b' }}>Η παραγγελία ακυρώθηκε</div>
            {cancelEntry && (
              <div style={{ fontSize: 12, color: '#7f1d1d', marginTop: 2 }}>
                {format(new Date(cancelEntry.changed_at), 'd MMM yyyy, HH:mm', { locale: el })}
                {cancelEntry.changed_by_name && ` · από ${cancelEntry.changed_by_name}`}
                {cancelEntry.note && ` · "${cancelEntry.note}"`}
              </div>
            )}
          </div>
        </div>
        <EditLog history={history} />
      </>
    );
  }

  const currentIdx = STEP_INDEX[currentStatus] ?? -1;

  // Build a map of step → timestamp from history (EDITED entries are skipped
  // because they aren't steps on this track)
  const tsByStatus = {};
  for (const h of history) {
    if (h.to_status === 'EDITED') continue;
    if (!tsByStatus[h.to_status]) tsByStatus[h.to_status] = h.changed_at;
  }

  return (
    <>
      <div style={{
        background: 'white',
        borderRadius: 10,
        padding: '18px 14px 14px',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        position: 'relative',
      }}>
        {STEPS.map((step, i) => {
          const isDone = i < currentIdx;
          const isCurrent = i === currentIdx;
          const isFuture = i > currentIdx;
          const ts = tsByStatus[step.key];

          const fillColor = isDone ? '#10b981' : isCurrent ? '#3b82f6' : '#e5e7eb';
          const labelColor = isFuture ? 'var(--gray-400)' : 'var(--gray-800)';

          return (
            <React.Fragment key={step.key}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: '0 0 auto', minWidth: 72, zIndex: 1 }}>
                <div style={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  background: fillColor,
                  color: isFuture ? 'var(--gray-500)' : 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 16,
                  marginBottom: 6,
                  animation: isCurrent ? 'pulse 1.5s ease-in-out infinite' : 'none',
                  boxShadow: isCurrent ? '0 0 0 4px rgba(59, 130, 246, 0.2)' : 'none',
                  transition: 'all 0.3s',
                }}>
                  {isDone ? '✓' : step.icon}
                </div>
                <div style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: labelColor,
                  textTransform: 'uppercase',
                  letterSpacing: 0.3,
                  textAlign: 'center',
                }}>
                  {step.label}
                </div>
                {ts ? (
                  <div style={{ fontSize: 10, color: 'var(--gray-400)', marginTop: 2, textAlign: 'center' }}>
                    {format(new Date(ts), 'HH:mm')}
                    <br />
                    <span style={{ fontSize: 9 }}>{format(new Date(ts), 'd MMM', { locale: el })}</span>
                  </div>
                ) : (
                  <div style={{ fontSize: 10, color: 'var(--gray-300)', marginTop: 2 }}>—</div>
                )}
              </div>
              {i < STEPS.length - 1 && (
                <div style={{
                  flex: 1,
                  height: 3,
                  background: i < currentIdx ? '#10b981' : '#e5e7eb',
                  marginTop: 16,
                  borderRadius: 2,
                  transition: 'background 0.3s',
                }} />
              )}
            </React.Fragment>
          );
        })}
        <style>{`
          @keyframes pulse {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.08); }
          }
        `}</style>
      </div>
      <EditLog history={history} />
    </>
  );
}
